import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ShippoHttpClientError } from '~/src/clients/shippo/ShippoHttpClientErrors';
import { useSnackbar } from '~/src/hooks/useSnackbar';
import {
  resendCode as apiResendCode,
  sendCode as apiSendCode,
  verifyCode as apiVerifyCode,
  type EmailVerificationReason,
} from '~/src/services/EmailVerificationService';

const KNOWN_ERROR_REASONS: EmailVerificationReason[] = [
  'otc_code_expired',
  'otc_email_send_failed',
  'otc_invalid_code',
  'otc_invalid_code_format',
  'otc_max_attempts_exceeded',
  'otc_missing_code',
];

const extractErrorReason = (error: unknown): EmailVerificationReason | undefined => {
  if (error instanceof ShippoHttpClientError) {
    return error.response?.reason as EmailVerificationReason | undefined;
  }
  return undefined;
};

const getErrorKey = (reason?: EmailVerificationReason) => {
  if (!reason || !KNOWN_ERROR_REASONS.includes(reason)) return 'otc_email_send_failed';
  return reason;
};

type UseEmailVerificationOptions = {
  onSuccess?: () => void;
};

type UseEmailVerificationReturn = {
  clearError: () => void;
  errorMessage: null | string;
  isResendCodeLoading: boolean;
  isSendCodeLoading: boolean;
  isVerifyingCode: boolean;
  sendCode: (resend?: boolean) => Promise<void>;
  verifyCode: (code: string) => Promise<void>;
};

export const useEmailVerification = ({
  onSuccess,
}: UseEmailVerificationOptions): UseEmailVerificationReturn => {
  const { t } = useTranslation('email-verification');
  const { show } = useSnackbar();

  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isSendCodeLoading, setIsSendCodeLoading] = useState(false);
  const [isResendCodeLoading, setIsResendCodeLoading] = useState(false);
  const [errorReason, setErrorReason] = useState<EmailVerificationReason | null>(null);

  const clearError = () => setErrorReason(null);

  const errorMessage = errorReason ? t(`otc.errors.${errorReason}`) : null;

  const sendCode = async (resend = false) => {
    if (resend) setIsResendCodeLoading(true);
    else setIsSendCodeLoading(true);

    setErrorReason(null);

    try {
      if (resend) {
        await apiResendCode();
        show({ message: t('otc.codeSent'), variant: 'success' });
      } else {
        await apiSendCode();
      }
    } catch (error) {
      const reason = getErrorKey(extractErrorReason(error));
      setErrorReason(reason);
    } finally {
      if (resend) setIsResendCodeLoading(false);
      else setIsSendCodeLoading(false);
    }
  };

  const verifyCode = async (code: string) => {
    setErrorReason(null);
    setIsVerifyingCode(true);

    try {
      const result = await apiVerifyCode(code);
      if (result.reason === 'success' || result.reason === 'otc_email_verified') {
        show({ message: t('otc.verificationSuccess'), variant: 'success' });
        onSuccess?.();
      } else {
        setErrorReason(result.reason);
      }
    } catch (error) {
      const reason = getErrorKey(extractErrorReason(error));
      setErrorReason(reason);
    } finally {
      setIsVerifyingCode(false);
    }
  };

  return {
    clearError,
    errorMessage,
    isResendCodeLoading,
    isSendCodeLoading,
    isVerifyingCode,
    sendCode,
    verifyCode,
  };
};
